// ─── Subscriber Intake Contract ──────────────────────────────────────────────
//
// The structured input that feeds the OCNexus instantiation orchestrator
// (ocn-S288). This is what a subscriber provides during factory onboarding.
//
// Used by: factory-instantiation-orchestrator.ts, manifest-builder.ts,
// and the subscriber-chat intake flow.
import { z } from 'zod';
/** The five built-in theme presets (mirrors saas-dna manifest.ts ThemePreset). */
export const THEME_PRESETS = ['clean-light', 'dark-first', 'fintech-trust', 'warm', 'bold-brand'];
export const THEME_PRESET_LABELS = {
    'clean-light': 'Clean Light',
    'dark-first': 'Dark First',
    'fintech-trust': 'Fintech Trust',
    'warm': 'Warm',
    'bold-brand': 'Bold Brand',
};
export const THEME_PRESET_DESCRIPTIONS = {
    'clean-light': 'Crisp white surfaces with blue accent — enterprise SaaS default',
    'dark-first': 'Dark charcoal with indigo accent — developer / tool products',
    'fintech-trust': 'Deep navy with emerald accent — finance, trust, compliance',
    'warm': 'Cream surfaces with amber accent — approachable consumer products',
    'bold-brand': 'Pure black with vivid purple — agency, creative, high-contrast',
};
/**
 * SubscriberIntake — the user-facing fields collected during factory onboarding.
 *
 * The manifest builder derives the full InfraTemplateManifest from this,
 * filling in env-var conventions, Doppler project slugs, etc.
 */
export const subscriberIntakeSchema = z.object({
    // ── Product identity ──────────────────────────────────────────────────────
    /** Human-readable display name. E.g. "Acme Analytics" */
    productName: z.string().min(1).max(100),
    /** URL-safe slug. E.g. "acme-analytics". Derived from productName when omitted. */
    productSlug: z.string().regex(/^[a-z][a-z0-9-]*$/, 'Must be lowercase kebab-case'),
    /** Short tagline shown on auth page and portal welcome. */
    productTagline: z.string().min(1).max(200),
    // ── Tenant ────────────────────────────────────────────────────────────────
    /** Canonical tenant identifier (Doppler project prefix, DB tenant_id). */
    tenantId: z.string().min(1).max(100),
    // ── Theme / Brand ─────────────────────────────────────────────────────────
    /** Theme preset selection. Default: 'clean-light'. */
    theme: z.enum(THEME_PRESETS).default('clean-light'),
    /** Absolute HTTPS URL to logo PNG/WebP. Empty string = text logo fallback. */
    logoUrl: z.string().optional().default(''),
    /** Alt text for logo (required if logoUrl is set). */
    logoAlt: z.string().optional().default(''),
    /**
     * Optional brand color overrides applied on top of the preset.
     * Restricted to --shell-* CSS custom properties.
     */
    themeOverrides: z.record(z.string()).optional(),
    // ── Domains ───────────────────────────────────────────────────────────────
    /** Apex domain for subscriber portal. E.g. "app.acme.com" */
    appDomain: z.string().min(1),
    /** Apex domain for admin portal. E.g. "admin.acme.com" */
    adminDomain: z.string().min(1),
    // ── Contact ───────────────────────────────────────────────────────────────
    /** Support / feedback email shown in portal footer. */
    supportEmail: z.string().email(),
    // ── Optional i18n overrides ───────────────────────────────────────────────
    /**
     * Partial overrides for shell UI copy (auth headings, nav labels, etc.).
     * Keys must match ShellStrings field names; values replace defaults.
     */
    stringOverrides: z.record(z.string()).optional(),
    // ── Advanced ──────────────────────────────────────────────────────────────
    /** Enable per-tenant custom domains (CNAME white-label seam). Default: false. */
    customDomainsEnabled: z.boolean().optional().default(false),
});
export function parseSubscriberIntake(input) {
    return subscriberIntakeSchema.parse(input);
}
//# sourceMappingURL=subscriber-intake.js.map